import { Redo2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DocumentEditorApi } from "./useDocumentEditor";

const IS_MAC =
  typeof navigator !== "undefined" && navigator.userAgent.includes("Mac");
const UNDO_SHORTCUT = IS_MAC ? "⌘Z" : "Ctrl+Z";
const REDO_SHORTCUT = IS_MAC ? "⇧⌘Z" : "Ctrl+Y";

interface UndoRedoControlsProps {
  /** The editor whose history the buttons step through. */
  editor: DocumentEditorApi;
  /** Blocks editing (an image upload is in flight). */
  disabled: boolean;
}

/**
 * The toolbar's undo and redo buttons, mirroring the editor's keyboard
 * history shortcuts for pointer and touch users. They disable while an inline
 * text session is open - the session owns its own history until it commits -
 * and when the corresponding history direction is empty.
 * @param editor - The editor whose history the buttons step through
 * @param disabled - Blocks editing
 * @returns The undo and redo buttons
 */
export function UndoRedoControls({ editor, disabled }: UndoRedoControlsProps) {
  const historyDisabled = disabled || editor.textEdit !== null;
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        title={`Undo (${UNDO_SHORTCUT})`}
        aria-label="Undo"
        disabled={historyDisabled || !editor.canUndo}
        onClick={editor.undo}
      >
        <Undo2 className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        title={`Redo (${REDO_SHORTCUT})`}
        aria-label="Redo"
        disabled={historyDisabled || !editor.canRedo}
        onClick={editor.redo}
      >
        <Redo2 className="size-4" />
      </Button>
    </div>
  );
}
